import readXlsxFile from "read-excel-file/browser";
import writeXlsxFile from "write-excel-file/browser";

const normalizeCellValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    if (value instanceof Date) {
      return value.toISOString();
    }

    if (Array.isArray(value.richText)) {
      return value.richText.map((item) => item.text || "").join("");
    }

    if (typeof value.text === "string") {
      return value.text;
    }

    if (value.hyperlink) {
      return value.text || value.hyperlink;
    }

    if (value.result !== undefined) {
      return value.result;
    }
  }

  return value;
};

const tableToRows = (tableElement) => {
  if (!tableElement) {
    return [];
  }

  return Array.from(tableElement.querySelectorAll("tr")).map((row) =>
    Array.from(row.querySelectorAll("th, td")).map((cell) => {
      const text = cell?.innerText || cell?.textContent || "";
      return text.replaceAll("\u00a0", " ").trim();
    })
  );
};

export const exportAoaToExcel = async (
  rows,
  fileName,
  transformCell,
  sheetName = "Sheet1"
) => {
  // Backward compatibility: some call sites still pass (rows, fileName, sheetName, transformCell)
  let resolvedTransformCell = transformCell;
  let resolvedSheetName = sheetName;

  if (typeof transformCell === "string") {
    resolvedSheetName = transformCell;
    resolvedTransformCell =
      typeof sheetName === "function" ? sheetName : undefined;
  }

  const excelRows = rows.map((rowValues, rowIndex) =>
    rowValues.map((value, columnIndex) => {
      let cellValue = normalizeCellValue(value);

      if (typeof resolvedTransformCell === "function") {
        const nextValue = resolvedTransformCell({
          value: cellValue,
          rowIndex,
          columnIndex,
        });

        if (nextValue !== undefined) {
          cellValue = nextValue;
        }
      }

      return { value: cellValue };
    })
  );

  return writeXlsxFile(excelRows, {
    fileName,
    sheet: resolvedSheetName,
  });
};

export const exportTableToExcel = async (
  tableElement,
  fileName,
  transformCell,
  sheetName = "Sheet1"
) => {
  // Backward compatibility: some call sites still pass (tableElement, fileName, sheetName, transformCell)
  let resolvedTransformCell = transformCell;
  let resolvedSheetName = sheetName;

  if (typeof transformCell === "string") {
    resolvedSheetName = transformCell;
    resolvedTransformCell =
      typeof sheetName === "function" ? sheetName : undefined;
  }

  const rows = tableToRows(tableElement);
  await exportAoaToExcel(
    rows,
    fileName,
    resolvedTransformCell,
    resolvedSheetName
  );
};

export const exportJsonToExcel = async (
  headers,
  data,
  fileName,
  sheetName = "Sheet1"
) => {
  const dataRows = (data || []).map((item) =>
    headers.map((header) => item?.[header] ?? "")
  );
  await exportAoaToExcel([headers, ...dataRows], fileName, undefined, sheetName);
};

export const aoaToCsv = (rows) => {
  const escapeCsv = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    if (/[,"\n]/.test(text)) {
      return `"${text.replaceAll('"', '""')}"`;
    }
    return text;
  };

  return rows.map((row) => row.map(escapeCsv).join(",")).join("\n");
};


export const readExcelFileToJson = async (file) => {
  const rows = await readXlsxFile(file);
  if (!rows.length) {
    return [];
  }
  const headers = rows[0]['data']?.[0].map((value) =>
    String(normalizeCellValue(value) || "").trim()
  );

  const jsonData = [];

  rows[0]?.['data'].slice(1).forEach((row) => {
    const item = {};
    let hasAnyValue = false;

    headers.forEach((header, index) => {
      if (!header) {
        return;
      }

      const value = normalizeCellValue(row[index]);
      item[header] = value === null || value === undefined ? "" : value;

      if (item[header] !== "") {
        hasAnyValue = true;
      }
    });

    if (hasAnyValue) {
      jsonData.push(item);
    }
  });

  return jsonData;
};